import { Input, Label } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { TellescopeFormFieldBaseProps } from "../types";
import { FieldDescription } from "../FieldDescription";
import { useTranslation } from "react-i18next";
import type { RepeatInputProps } from "../hooks/use-tellescope-enduser-form";

export interface StringFieldProps extends TellescopeFormFieldBaseProps<"string"> {
  repeat?: RepeatInputProps;
}

export const StringField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  options,
  error,
  onBlur,
  touched,
  disabled,
  required,
  repeat,
  autoFocus,
}: StringFieldProps) => {
  const { t } = useTranslation("forms");

  const resolvedPlaceholder = placeholder ?? t("Answer here...");
  const maxLength = options?.maxLength;
  const minLength = options?.minLength;
  const showCounter = typeof maxLength === "number" && maxLength > 0;
  const requireRepeat = !!options?.repeat;
  const showError = !!(error && touched);
  const showRepeatMismatch = !!repeat && repeat.repeatTouched && repeat.repeatMismatch;

  const currentLength = value?.length ?? 0;

  return (
    <div className="space-y-1">
      <Label required={required}>{title || resolvedPlaceholder}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={resolvedPlaceholder}
          maxLength={maxLength}
          minLength={minLength}
          className={cn(className, showError && "border-destructive", showCounter && "pr-16")}
          aria-invalid={showError}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        {showCounter && (
          <span className="absolute right-2 bottom-1 text-xs text-muted-foreground pointer-events-none">
            {t("{{current}}/{{max}}", { current: currentLength, max: maxLength })}
          </span>
        )}
      </div>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
      {requireRepeat && !!repeat && (
        <div className="space-y-1 pt-2">
          <Label required={required}>{t("Confirm {{title}}", { title: title ?? "" }).trim()}</Label>
          <Input
            value={repeat.repeatValue}
            onChange={(e) => repeat.onRepeatChange(e.target.value)}
            onBlur={repeat.onRepeatBlur}
            placeholder={resolvedPlaceholder}
            maxLength={maxLength}
            className={cn(className, showRepeatMismatch && "border-destructive")}
            aria-invalid={showRepeatMismatch}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
